import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";

import CreateCoursePopup from "../../common/createPopup";
import CreatePagePopup from "../../common/CreatePagePopup";
import PagesComponent from "../../common/PagesComponent";
import MarkdownViewer from "../../common/MarkdownViewer";
import MCQViewer from "../../common/MCQViewer";
import TrueFalseViewer from "../../common/TrueFalseViewer";
import MatchViewer from "../../common/MatchViewer";
import FillUpsViewer from "../../common/FillUpsViewer";
import AnalysisComponent from "../../common/AnalysisComponent";
import ContentGeneratorPopup from "../../common/ContentGeneratorPopup";
import MarkdownEditorPopup from "../../common/MarkdownEditorPopup";
import ContentToolbarWrapperComponent from "../../common/ContentToolbarWrapper";
import GlobalLoader from "../../common/GlobalLoader";
import EvalManualPopup from "../../common/EvalManualPopup";
import EvalScoreDetailsPopup from "../../common/EvalScoreDetailsPopup";
import ViewMaterialPopup from "../../common/ViewMaterialPopup";
import MaterialViewer from "../../common/MaterialViewer";
import UploadMaterialPopup from "../../common/UploadMaterialPopup";
import Cookies from "js-cookie";
import checkImg from "../../../assets/image/checkImage.png";

import {
  getPagesForSubtopic,
  getPageForSubtopic,
  uploadPageContentFile,
  removePageContentFile,
} from "../../../helper/apicalls/course";
import {
  getSubtopicPageEvalStatus,
  getSubtopicEvalPageContent,
  evalWithAiMark,
  createSubtopicEvalManually,
} from "../../../helper/apicalls/eval";
import "./../../../styles/admin/subtopics.scss";
import EmptyPage from "../../common/emptyPage";
import SuccessPopup from "../../common/SuccessPopup";

const SubTopicDetails = () => {
  const { key, subkey, topicKey, subTopicKey } = useParams();
  const location = useLocation();
  const role = Cookies.get("role");
  const permissions = Cookies.get("permissions");
  const permissionsData = permissions ? JSON.parse(permissions) : [];

  const [openCreateSubTopicPopup, setOpenCreateSubTopicPopup] = useState(false);
  const [openCreatePagePopup, setOpenCreatePagePopup] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedChildMaterial, setSelectedChildMaterial] = useState(null);
  const [cachePages, setCachePages] = useState([]);
  const [removeCache, setRemoveCache] = useState(false);
  const [evalStatusByPageId, setEvalStatusByPageId] = useState({});
  const [isEvalScorePopupOpen, setIsEvalScorePopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isManualEvalOpen, setIsManualEvalOpen] = useState(false);
  const [manualEvalSaving, setManualEvalSaving] = useState(false);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [isReplaceMode, setIsReplaceMode] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isUploadMaterialOpen, setIsUploadMaterialOpen] = useState(false);
  const [isMaterialPopupOpen, setIsMaterialPopupOpen] = useState(false);
  const [selectedMaterialForPopup, setSelectedMaterialForPopup] =
    useState(null);

  const isEvalPath = location.pathname.startsWith("/admin/eval");
  const [addAfterIndex, setAddAfterIndex] = useState(null);
  const [isAudioGenerated, setIsAudioGenerated] = useState(false);

  const getEvalStatusForPage = (page) => {
    if (!page) return null;
    const id = page?.id;
    const pageKey = page?.key;
    const level = page?.level;
    const virtualKey = level && id ? `${id}-${level}` : null;
    return (
      (virtualKey && evalStatusByPageId[virtualKey]) ||
      (id && evalStatusByPageId[id]) ||
      (pageKey && evalStatusByPageId[pageKey]) ||
      null
    );
  };

  const isPageEvaluated = (page) => {
    if (!page || !page.id) return false;
    if (page.id === "default-page") return true;
    const status = getEvalStatusForPage(page);
    if (status && typeof status.evaluated !== "undefined")
      return !!status.evaluated;
    if (page.is_eval_content) return true;
    if (page.evalution_score) return true;
    if (
      page.overall_score !== null &&
      typeof page.overall_score !== "undefined"
    )
      return true;
    return false;
  };

  const hasContentToEvaluate = (page) => {
    if (!page) return false;
    const levelStr = (page.level || "").toLowerCase();
    const contentToCheck =
      levelStr === "medium" ? page.content
      : levelStr === "hard" ? page.content
      : page.content;
    
    if (!contentToCheck) return false;
    if (Array.isArray(contentToCheck)) return contentToCheck.length > 0;
    if (typeof contentToCheck === "string") {
      try {
        const parsed = JSON.parse(contentToCheck);
        return Array.isArray(parsed) ? parsed.length > 0 : !!parsed;
      } catch (e) {
        return contentToCheck.trim().length > 0;
      }
    }
    return typeof contentToCheck === "object";
  };

  const isContentPage = (page) => {
    if (!page || !page.id) return false;
    if (page.id === "default-page") return false;
    // Test level sub-pages (easy/medium/hard) are evaluable — level field is merged in
    // from the virtual page on selection; is_virtual may not be present in API response
    if (page.page_type === "test" && page.level) return true;
    if (page.is_virtual) return false;
    if (Array.isArray(page.child_pages) && page.child_pages.length > 0)
      return false;
    const type = (page.page_type || "").toLowerCase();
    if (
      type === "quiz" ||
      type === "test" ||
      type === "active_recall" ||
      type === "materials" ||
      type === "material_item"
    )
      return false;
    return true;
  };

  const normalizePageData = (page, payload, extras = {}) => {
    const merged = { ...(page || {}), ...(payload || {}), ...(extras || {}) };
    const normalizedContent =
      typeof merged.content !== "undefined"
        ? merged.content
        : typeof merged.page_content !== "undefined"
          ? merged.page_content
          : typeof merged.pageContent !== "undefined"
            ? merged.pageContent
            : merged.content;

    const normalizedMediumContent =
      typeof merged.medium_content !== "undefined"
        ? merged.medium_content
        : typeof merged.medium_page_content !== "undefined"
          ? merged.medium_page_content
          : typeof merged.mediumContent !== "undefined"
            ? merged.mediumContent
            : merged.medium_content;

    const normalizedHardContent =
      typeof merged.hard_content !== "undefined"
        ? merged.hard_content
        : typeof merged.hard_page_content !== "undefined"
          ? merged.hard_page_content
          : typeof merged.hardContent !== "undefined"
            ? merged.hardContent
            : merged.hard_content;

    return {
      ...merged,
      content: normalizedContent,
      medium_content: normalizedMediumContent,
      hard_content: normalizedHardContent,
    };
  };

  const fetchFirstPageData = async (firstPage) => {
    if (!firstPage) return;
    setLoading(true);
    let targetPage = firstPage;
    if (firstPage.child_pages && firstPage.child_pages.length > 0) {
      targetPage = firstPage.child_pages[0];
    }
    const targetId = targetPage.id;
    if (targetId && targetId !== "default-page") {
      try {
        const fullPageData = await getPageForSubtopic(
          subTopicKey,
          targetId,
          targetPage.level || firstPage.level || null,
          !!(targetPage.is_admin_page || firstPage.is_admin_page)
        );
        const result = {
          ...fullPageData,
          level: targetPage.level || fullPageData.level,
          is_admin_page: !!(fullPageData.is_admin_page || targetPage.is_admin_page || firstPage.is_admin_page),
        };
        handlePageSelect(result);
      } catch (error) {
        console.error("Error fetching first page data:", error);
        handlePageSelect(targetPage);
      }
    } else {
      handlePageSelect(targetPage);
    }
    setLoading(false);
  };

  const handleEvalManuallyOpen = () => {
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;
    setIsManualEvalOpen(true);
  };

  const handleEvalManuallySave = async (scores) => {
    if (!selectedPage || !selectedPage.id) return;
    if (!isContentPage(selectedPage)) return;
    try {
      setManualEvalSaving(true);
      await createSubtopicEvalManually({
        key: subTopicKey,
        pageId: selectedPage.id,
        evalution_score: scores,
      });
      setIsManualEvalOpen(false);
      await fetchEvalStatus();
      await handlePageSelect(selectedPage);
    } catch (err) {
      console.error("Error saving manual eval:", err);
    } finally {
      setManualEvalSaving(false);
    }
  };

  const fetchPages = async () => {
    try {
      setLoading(true);
      const data = await getPagesForSubtopic(subTopicKey);
      if (Array.isArray(data)) {
        setPages(data);
      } else if (
        data &&
        typeof data === "object" &&
        data.hasOwnProperty("id")
      ) {
        setPages([data]);
      } else {
        setPages([]);
      }
    } catch (err) {
      console.error("Error fetching pages:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvalStatus = async () => {
    if (role !== "admin") return;
    try {
      const status = await getSubtopicPageEvalStatus(subTopicKey);
      setEvalStatusByPageId(status?.byPageId || {});
    } catch (err) {
      console.error("Error fetching eval status for subtopic pages:", err);
      setEvalStatusByPageId({});
    }
  };

  const handleCloseDialog = () => {
    setOpenCreateSubTopicPopup(false);
  };

  const handleClosePagePopup = () => {
    setOpenCreatePagePopup(false);
  };

  const handleRefreshPages = () => {
    fetchPages();
  };

  const handleEvalWithAi = async () => {
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;
    try {
      setLoading(true);
      const level = selectedPage?.level || null;
      const aiResp = await evalWithAiMark({
        title: "subtopic",
        key: subTopicKey,
        pageId: selectedPage.id,
        level,
      });

      const aiPayload = aiResp?.data ?? aiResp;

      let evalution_score =
        aiPayload?.structured?.evalution_score ??
        aiPayload?.structured?.evalutionScore ??
        null;

      if (
        !evalution_score &&
        aiPayload?.content &&
        typeof aiPayload.content === "string"
      ) {
        try {
          const parsed = JSON.parse(aiPayload.content);
          evalution_score =
            parsed?.evalution_score ??
            parsed?.evalutionScore ??
            parsed?.evaluation_score ??
            parsed?.evaluationScore ??
            parsed?.scores ??
            null;
        } catch (e) {}
      }

      if (!evalution_score) {
        evalution_score =
          aiPayload?.evalution_score ??
          aiPayload?.evalutionScore ??
          aiPayload?.evaluation_score ??
          aiPayload?.evaluationScore ??
          aiPayload?.scores ??
          aiPayload?.result?.evalution_score ??
          aiPayload?.result?.evalutionScore ??
          aiPayload?.result?.evaluation_score ??
          aiPayload?.result?.evaluationScore ??
          aiPayload?.result?.scores ??
          null;
      }

      if (evalution_score) {
        await createSubtopicEvalManually({
          key: subTopicKey,
          pageId: selectedPage.id,
          evalution_score,
        });
      }
      await fetchEvalStatus();
      await handlePageSelect(selectedPage);
    } catch (err) {
      console.error("Error running eval with AI:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSelect = async (page) => {
    if (!page || !page.id || !isEvalPath) {
      setSelectedPage(page);
      return;
    }

    if (!isContentPage(page)) {
      setSelectedPage(page);
      return;
    }

    const status = getEvalStatusForPage(page);
    if (status && status.evaluated && page.id !== "default-page") {
      try {
        setLoading(true);
        const evalData = await getSubtopicEvalPageContent(
          status?.evaluated_id || page.id,
        );
        const payload = evalData?.data ?? evalData;
        if (payload) {
          const overall_score =
            payload.overall_score ?? payload.overallScore ?? null;
          const evalution_score =
            payload.evalution_score ??
            payload.evalutionScore ??
            payload.evaluation_score ??
            payload.evaluationScore ??
            payload.scores ??
            null;
          setSelectedPage(
            normalizePageData(page, payload, {
              page_type: payload.page_type || page.page_type,
              is_eval_content: true,
              overall_score,
              evalution_score,
            }),
          );
        } else {
          setSelectedPage(page);
        }
      } catch (err) {
        console.error("Error fetching eval content:", err);
        setSelectedPage(page);
      } finally {
        setLoading(false);
      }
    } else {
      if (page.id === "default-page") {
        setSelectedPage(page);
        return;
      }

      setSelectedPage(
        normalizePageData(page, null, {
          is_eval_content: false,
        }),
      );
    }
  };

  const handleOpenEvalScoreDetails = async () => {
    if (!isEvalPath) return;
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;

    const status = getEvalStatusForPage(selectedPage);
    const evaluated = isPageEvaluated(selectedPage);
    if (!evaluated) return;

    try {
      setLoading(true);
      const evalData = await getSubtopicEvalPageContent(
        status?.evaluated_id || selectedPage.id,
      );
      const payload = evalData?.data ?? evalData;

      if (payload) {
        const overall_score =
          payload.overall_score ?? payload.overallScore ?? null;
        const evalution_score =
          payload.evalution_score ??
          payload.evalutionScore ??
          payload.evaluation_score ??
          payload.evaluationScore ??
          payload.scores ??
          null;

        setSelectedPage((prev) =>
          normalizePageData(prev || selectedPage, payload, {
            page_type: payload.page_type || (prev || selectedPage)?.page_type,
            is_eval_content: true,
            overall_score,
            evalution_score,
          }),
        );
      }

      setIsEvalScorePopupOpen(true);
    } catch (err) {
      console.error("Error fetching eval content:", err);
      setIsEvalScorePopupOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const renderEvalScoreChips = () => {
    if (!isEvalPath || !selectedPage || !selectedPage.id) return null;
    if (selectedPage.id === "default-page") return null;
    if (!isContentPage(selectedPage)) return null;

    const status = getEvalStatusForPage(selectedPage);
    const evaluated = isPageEvaluated(selectedPage);
    if (!evaluated) return null;

    const scores = selectedPage.evalution_score || {};
    const overall =
      status &&
      status.overall_score !== null &&
      typeof status.overall_score !== "undefined"
        ? status.overall_score
        : selectedPage.overall_score !== null &&
            typeof selectedPage.overall_score !== "undefined"
          ? selectedPage.overall_score
          : scores.evalution_score;

    const items = [
      { key: "evalution_score", value: overall },
      { key: "quality_score", value: scores.quality_score },
      { key: "relevance_score", value: scores.relevance_score },
      { key: "grounding_score", value: scores.grounding_score },
      { key: "reasoning_score", value: scores.reasoning_score },
      { key: "risk_score", value: scores.risk_score },
    ];

    const formatLabel = (raw) => {
      if (!raw) return "";
      return String(raw)
        .replace(/_/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    };

    const toPct = (val) => {
      const num = Number(val);
      if (!Number.isFinite(num)) return 0;
      return Math.max(0, Math.min(100, (num / 10) * 100));
    };

    return (
      <div className="eval-score-actions">
        <button
          type="button"
          className="primary-btn eval-view-details-btn"
          onClick={handleOpenEvalScoreDetails}
        >
          View Evaluate Details
        </button>

        <EvalScoreDetailsPopup
          open={isEvalScorePopupOpen}
          onClose={() => setIsEvalScorePopupOpen(false)}
          overall={overall}
          scores={scores}
        />
      </div>
    );
  };

  const handleUpdatePageContent = (updatedPageData) => {
    setSelectedPage(updatedPageData);
    const updatedPages = pages.map((page) =>
      page.id === updatedPageData.id ? updatedPageData : page,
    );
    setRemoveCache(true);
    setPages(updatedPages);
  };

  // After AI generation saves content, refresh pages list and eval status
  const handleGeneratorSave = async (updatedPageData) => {
    handleUpdatePageContent(updatedPageData);
    try {
      await fetchPages();
      await fetchEvalStatus();
      setTimeout(() => fetchEvalStatus(), 3000);
      setTimeout(() => fetchEvalStatus(), 10000);
    } catch (err) {
      console.error("Error refreshing after generator save:", err);
    }
  };

  const handleRemoveMaterial = async (materialId) => {
    if (!selectedPage || !selectedPage.id) return;

    try {
      setLoading(true);

      await removePageContentFile({
        pageid: selectedPage.id,
        material_id: materialId,
        course_key: key,
        subject_key: subkey,
        topic_key: topicKey,
        subtopic_key: subTopicKey,
      });

      const updatedPageData = await getPageForSubtopic(
        subTopicKey,
        selectedPage.id,
        selectedPage.level || null,
        !!selectedPage.is_admin_page
      );

      setSelectedPage(updatedPageData);
      setRemoveCache(true);

      await fetchPages();
    } catch (error) {
      console.error("Error removing material:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMaterial = async (materialData) => {
    if (!selectedPage || !selectedPage.id) return;

    try {
      setLoading(true);
      const uploadResponse = await uploadPageContentFile({
        title: "subtopic",
        key: materialData.key,
        pageid: materialData.pageid,
        type: materialData.type,
        file: materialData.file,
        url: materialData.url,
        name: materialData.name,
      });

      const updatedPageData = await getPageForSubtopic(
        subTopicKey,
        selectedPage.id,
        selectedPage.level || null,
        !!selectedPage.is_admin_page
      );

      setSelectedPage(updatedPageData);
      setRemoveCache(true);

      await fetchPages();
    } catch (error) {
      console.error("Error uploading material:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedPage(null);
    fetchPages();
    fetchEvalStatus();
  }, [subTopicKey, topicKey, location]);

  useEffect(() => {
    if (pages.length > 0 && !selectedPage) {
      fetchFirstPageData(pages[0]);
    }
  }, [pages, selectedPage]);

  useEffect(() => {
    setSelectedChildMaterial(null);
  }, [selectedPage]);

  const hasWritePermission = !isEvalPath && (
    role === "admin" ||
    ((role === "institution" || role === "teacher") && !selectedPage?.is_admin_page)
  );

  return (
    <div className="subtopics-container">
      <GlobalLoader visible={loading} text="Loading..." />

      <div className="subtopics-content">
        <div className="subtopic-form-container"></div>

        <div className="subtopic-content-container">
          {(() => {
            if (role !== "admin") return null;
            if (!selectedPage || !selectedPage.id) return null;
            if (selectedPage.id === "default-page") return null;
            if (!isContentPage(selectedPage)) return null;
            if (isPageEvaluated(selectedPage)) return null;
            if (!hasContentToEvaluate(selectedPage)) return null;

            return (
              <div className="eval-actions-bar">
                <button
                  type="button"
                  className="eval-btn"
                  onClick={handleEvalWithAi}
                >
                  Evaluate with AI
                </button>
              </div>
            );
          })()}

          {renderEvalScoreChips()}

          <EvalManualPopup
            open={isManualEvalOpen}
            onClose={() => setIsManualEvalOpen(false)}
            onSave={handleEvalManuallySave}
            saving={manualEvalSaving}
          />
          {selectedPage &&
          (selectedPage.content ||
            selectedPage.medium_content ||
            selectedPage.hard_content ||
            selectedPage.page_type === "materials") ? (
            (() => {
              if (selectedPage.page_type === "material_item") {
                return (
                  <ContentToolbarWrapperComponent
                    onEdit={undefined}
                    onAdd={undefined}
                    onUploadMaterials={
                      isEvalPath
                        ? undefined
                        : () => setIsUploadMaterialOpen(true)
                    }
                    pageType={selectedPage?.page_type}
                    permission={hasWritePermission}
                    hasContent={Boolean(selectedPage?.url || selectedPage?.file || selectedPage?.material_url || selectedPage?.name)}
                    materialType={selectedPage?.material_type || ""}
                    className={
                      role === "admin" &&
                      !!selectedPage?.id &&
                      selectedPage?.id !== "default-page" &&
                      isContentPage(selectedPage) &&
                      !isPageEvaluated(selectedPage) &&
                      hasContentToEvaluate(selectedPage)
                        ? "has-eval-actions"
                        : ""
                    }
                  >
                    <div className="content-wrapper">
                      <MaterialViewer
                        material={selectedPage}
                        pageData={selectedPage}
                        courseKey={key}
                        subjectKey={subkey}
                        topicKey={topicKey}
                        subTopicKey={subTopicKey}
                        onRemove={handleRemoveMaterial}
                        title="subtopic"
                      />
                    </div>
                  </ContentToolbarWrapperComponent>
                );
              }

              let contentArray = [];

              try {
                let contentField = selectedPage.content;
                const level = (selectedPage.level || "").toLowerCase();
                if (level === "medium" && selectedPage.medium_content) {
                  contentField = selectedPage.medium_content;
                } else if (level === "hard" && selectedPage.hard_content) {
                  contentField = selectedPage.hard_content;
                }

                if (contentField) {
                  if (typeof contentField === "string") {
                    const trimmed = contentField.trim();
                    if (trimmed) {
                      const parsedContent = JSON.parse(trimmed);
                      if (Array.isArray(parsedContent)) {
                        contentArray = parsedContent;
                      } else if (parsedContent) {
                        contentArray = [parsedContent];
                      }
                    }
                  } else if (Array.isArray(contentField)) {
                    contentArray = contentField;
                  } else if (typeof contentField === "object") {
                    contentArray = [contentField];
                  }
                }
              } catch (e) {
                contentArray = [{ content: selectedPage.content }];
              }

              const hasContent = contentArray && contentArray.length > 0;

              return (
                <ContentToolbarWrapperComponent
                  onEdit={
                    isEvalPath
                      ? undefined
                      : selectedPage?.page_type === "quiz"
                        ? undefined
                        : () => setIsEditorOpen(true)
                  }
                  onAdd={
                    isEvalPath
                      ? undefined
                      : () => {
                          setIsReplaceMode(false);
                          setIsGeneratorOpen(true);
                        }
                  }
                  onReplace={
                    isEvalPath
                      ? undefined
                      : () => {
                          setIsReplaceMode(true);
                          setIsGeneratorOpen(true);
                        }
                  }
                  onUploadMaterials={
                    isEvalPath || selectedPage?.page_type !== "material_item"
                      ? undefined
                      : () => setIsUploadMaterialOpen(true)
                  }
                  pageType={selectedPage?.page_type}
                  permission={hasWritePermission}
                  hasContent={hasContent}
                  audioBook={selectedPage?.tts_s3_url || null}
                  layer={"subtopic"}
                  id={selectedPage?.id}
                  className={
                    role === "admin" &&
                    !!selectedPage?.id &&
                    selectedPage?.id !== "default-page" &&
                    isContentPage(selectedPage) &&
                    !isPageEvaluated(selectedPage) &&
                    hasContentToEvaluate(selectedPage)
                      ? "has-eval-actions"
                      : ""
                  }
                  materialType={selectedPage?.material_type || ""}
                >
                  <div className="content-wrapper">
                    {contentArray?.length > 0 ? (
                      contentArray.map((item, index) => {
                        let contentType = "content";
                        let contentToDisplay = item;
                        let itemName = item.name || "Material";
                        let parsedItem = item;
                        if (typeof item === "string") {
                          try {
                            parsedItem = JSON.parse(item);
                            itemName = parsedItem.name || itemName;
                          } catch (e) {
                            parsedItem = item;
                          }
                        }

                        if (
                          parsedItem &&
                          typeof parsedItem === "object" &&
                          parsedItem.type
                        ) {
                          contentType = parsedItem.type;
                          contentToDisplay = parsedItem.content || parsedItem;
                          itemName = parsedItem.name || itemName;
                        } else if (
                          item &&
                          typeof item === "object" &&
                          item.type
                        ) {
                          contentType = item.type;
                          contentToDisplay = item.content;
                          itemName = item.name || itemName;
                        } else if (
                          item &&
                          typeof item === "object" &&
                          item.content
                        ) {
                          contentToDisplay = item.content;
                          itemName = item.name || itemName;
                        }

                        switch (contentType) {
                          case "material_item":
                          case "pdf":
                          case "video":
                          case "audio":
                          case "image":
                          case "ppt":
                          case "doc":
                            return (
                              <div key={`material-${index}`}>
                                <MaterialViewer
                                  material={item}
                                  pageData={selectedPage}
                                  courseKey={key}
                                  subjectKey={subkey}
                                  topicKey={topicKey}
                                  subTopicKey={subTopicKey}
                                  onRemove={handleRemoveMaterial}
                                  title="subtopic"
                                />
                              </div>
                            );
                          case "mcq":
                            return (
                              <MCQViewer
                                key={`mcq-${index}`}
                                questionData={contentToDisplay}
                                onUpdate={handleUpdatePageContent}
                                pageId={selectedPage.id}
                                pageData={selectedPage}
                                context="subtopic"
                                courseKey={key}
                                subjectKey={subkey}
                                topicKey={topicKey}
                                subTopicKey={subTopicKey}
                                contentIndex={index}
                                onAddNext={() => {
                                  setAddAfterIndex(index);
                                  setIsGeneratorOpen(true);
                                }}
                                permission={hasWritePermission}
                              />
                            );
                          case "trueorfalse":
                            return (
                              <TrueFalseViewer
                                key={`tf-${index}`}
                                questionData={contentToDisplay}
                                onUpdate={handleUpdatePageContent}
                                pageId={selectedPage.id}
                                pageData={selectedPage}
                                context="subtopic"
                                courseKey={key}
                                subjectKey={subkey}
                                topicKey={topicKey}
                                subTopicKey={subTopicKey}
                                contentIndex={index}
                                onAddNext={() => {
                                  setAddAfterIndex(index);
                                  setIsGeneratorOpen(true);
                                }}
                                permission={hasWritePermission}
                              />
                            );
                          case "match":
                            return (
                              <MatchViewer
                                key={`match-${index}`}
                                questionData={contentToDisplay}
                                onUpdate={handleUpdatePageContent}
                                pageId={selectedPage.id}
                                pageData={selectedPage}
                                context="subtopic"
                                courseKey={key}
                                subjectKey={subkey}
                                topicKey={topicKey}
                                subTopicKey={subTopicKey}
                                contentIndex={index}
                                onAddNext={() => {
                                  setAddAfterIndex(index);
                                  setIsGeneratorOpen(true);
                                }}
                                permission={hasWritePermission}
                              />
                            );
                          case "fillups":
                            return (
                              <FillUpsViewer
                                key={`fillups-${index}`}
                                questionData={contentToDisplay}
                                onUpdate={handleUpdatePageContent}
                                pageId={selectedPage.id}
                                pageData={selectedPage}
                                context="subtopic"
                                courseKey={key}
                                subjectKey={subkey}
                                topicKey={topicKey}
                                subTopicKey={subTopicKey}
                                contentIndex={index}
                                onAddNext={() => {
                                  setAddAfterIndex(index);
                                  setIsGeneratorOpen(true);
                                }}
                                permission={hasWritePermission}
                              />
                            );
                          case "analysis":
                            return (
                              <AnalysisComponent
                                key={`analysis-${index}`}
                                data={contentToDisplay}
                                title="Conversational Analysis"
                                onUpdate={handleUpdatePageContent}
                                pageId={selectedPage.id}
                                pageData={selectedPage}
                                context="subtopic"
                                courseKey={key}
                                subjectKey={subkey}
                                topicKey={topicKey}
                                subTopicKey={subTopicKey}
                                contentIndex={index}
                                onAddNext={() => {
                                  setAddAfterIndex(index);
                                  setIsGeneratorOpen(true);
                                }}
                                permission={hasWritePermission}
                              />
                            );
                          default:
                            return (
                              <MarkdownViewer
                                key={index}
                                content={
                                  typeof contentToDisplay === "string"
                                    ? contentToDisplay
                                    : JSON.stringify(contentToDisplay)
                                }
                                pageId={selectedPage.id}
                                pageData={selectedPage}
                                context="subtopic"
                                courseKey={key}
                                subjectKey={subkey}
                                topicKey={topicKey}
                                subTopicKey={subTopicKey}
                                onUpdate={handleUpdatePageContent}
                              />
                            );
                        }
                      })
                    ) : (
                      <>
                        <EmptyPage content={"No content Available"} />
                      </>
                    )}
                  </div>
                </ContentToolbarWrapperComponent>
              );
            })()
          ) : (
            <>
              <EmptyPage content={"No content Available"} />
            </>
          )}
        </div>

        <div className="pages-component-container">
          <div className="pages-component">
            <PagesComponent
              pages={pages}
              onAddPage={isEvalPath ? undefined : handleRefreshPages}
              subTopicKey={subTopicKey}
              onPageSelect={handlePageSelect}
              selectedPageData={selectedPage}
              removeCache={removeCache}
              setRemoveCache={setRemoveCache}
              readOnly={isEvalPath}
              evalStatusByPageId={evalStatusByPageId}
            />
          </div>
        </div>
      </div>

      {openCreateSubTopicPopup && !isEvalPath && (
        <CreateCoursePopup
          open={openCreateSubTopicPopup}
          handleClose={handleCloseDialog}
          title="Sub Topic"
        />
      )}
      {openCreatePagePopup && !isEvalPath && (
        <CreatePagePopup
          open={openCreatePagePopup}
          handleClose={handleClosePagePopup}
        />
      )}

      {isGeneratorOpen && !isEvalPath && (
        <ContentGeneratorPopup
          open={isGeneratorOpen}
          handleClose={() => {
            setIsGeneratorOpen(false);
            setAddAfterIndex(null);
            setIsReplaceMode(false);
          }}
          onSave={handleGeneratorSave}
          isReplaceMode={isReplaceMode}
          existingContent={
            (selectedPage?.level === "medium"
              ? selectedPage?.medium_content
              : selectedPage?.level === "hard"
              ? selectedPage?.hard_content
              : selectedPage?.content) || (pages.length > 0 ? pages[0].content : "")
          }
          pageId={selectedPage?.id || (pages.length > 0 ? pages[0].id : null)}
          pageData={selectedPage || (pages.length > 0 ? pages[0] : null)}
          context="subtopic"
          courseKey={key}
          subjectKey={subkey}
          topicKey={topicKey}
          subTopicKey={subTopicKey}
          insertionIndex={
            typeof addAfterIndex === "number"
              ? addAfterIndex
              : Array.isArray(selectedPage?.content)
                ? selectedPage.content.length - 1
                : -1
          }
        />
      )}

      {isAudioGenerated && (
        <SuccessPopup
          open={isAudioGenerated}
          onClose={() => setIsAudioGenerated(false)}
          iconSrc={checkImg}
        />
      )}

      {isEditorOpen && !isEvalPath && (
        <MarkdownEditorPopup
          open={isEditorOpen}
          handleClose={() => setIsEditorOpen(false)}
          title="Edit Content"
          initialValue={
            selectedPage?.content || (pages.length > 0 ? pages[0].content : "")
          }
          onSave={(updatedPageData) => {
            handleUpdatePageContent(updatedPageData);
            setIsEditorOpen(false);
          }}
          context="subtopic"
          pageId={selectedPage?.id || (pages.length > 0 ? pages[0].id : null)}
          pageData={selectedPage || (pages.length > 0 ? pages[0] : null)}
          courseKey={key}
          subjectKey={subkey}
          topicKey={topicKey}
          subTopicKey={subTopicKey}
        />
      )}

      {isUploadMaterialOpen && !isEvalPath && (
        <UploadMaterialPopup
          open={isUploadMaterialOpen}
          handleClose={() => setIsUploadMaterialOpen(false)}
          onSave={handleUploadMaterial}
          materialType={selectedPage?.material_type || ""}
          pageData={
            selectedPage?.page_type === "materials"
              ? selectedChildMaterial
              : selectedPage
          }
          context="subtopic"
          courseKey={key}
          subjectKey={subkey}
          topicKey={topicKey}
          subTopicKey={subTopicKey}
        />
      )}

      <ViewMaterialPopup
        open={isMaterialPopupOpen}
        handleClose={() => setIsMaterialPopupOpen(false)}
        material={selectedMaterialForPopup}
      />
    </div>
  );
};

export default SubTopicDetails;
